/* eslint-disable react/jsx-no-bind */
import React, {useState, useContext} from 'react';
import PropTypes from 'prop-types';
import {get} from 'lodash';

import GuestPicker from '@homeaway/react-guest-picker';

import MaxGuests from './MaxGuests/MaxGuests';

import translate from '../../../../../utils/translations';
import StayContext from '../../../../context/StayContext';

const getChildrenAllowedFromHouseRules = (houseRules) =>
    get(houseRules, 'children.allowed', true);

const getMaxAdultsFromHouseRules = (houseRules, sleeps) =>
    get(houseRules, 'maxOccupancy.adults') ||
    get(houseRules, 'maxOccupancy.guests') ||
    sleeps;

const getMaxChildrenFromHouseRules = (houseRules, sleeps) => {
    const childrenAllowed = getChildrenAllowedFromHouseRules(houseRules);
    return !childrenAllowed ? 0 : sleeps && sleeps - 1;
};

const getSpaceLeft = (adults, children, sleeps) => {
    const spaceMargin = sleeps - adults - children;
    return Math.max(0, spaceMargin);
};

export default function ModifyBookingGuestPicker({
    houseRules,
    setUpdatedGuests,
    sleeps,
    translations,
}) {
    const {
        stay: {
            booking: {
                guests: {numberOfAdults, numberOfChildren},
            },
        },
    } = useContext(StayContext);
    const [guestValues, setGuestValues] = useState({
        adultsCount: numberOfAdults,
        childrenCount: numberOfChildren,
    });
    const {adultsCount, childrenCount} = guestValues;

    const onGuestValuesChange = (key, value) => {
        setGuestValues({
            ...guestValues,
            [key]: value,
        });
    };

    // Update guest count on guest picker close/exit.
    const updateGuests = () => {
        setUpdatedGuests({
            newAdultsCount: adultsCount,
            newChildrenCount: childrenCount,
        });
    };

    const adultsMax = getMaxAdultsFromHouseRules(houseRules, sleeps);
    const childrenMax = getMaxChildrenFromHouseRules(houseRules, sleeps);
    const isChildrenAllowed = getChildrenAllowedFromHouseRules(houseRules);
    const spaceLeft = getSpaceLeft(adultsCount, childrenCount, sleeps);
    const maxAdultsForCounter = Math.min(adultsMax, adultsCount + spaceLeft);
    const maxChildrenForCounter = Math.min(
        childrenMax,
        childrenCount + spaceLeft
    );

    const adultsLabel =
        adultsCount === 1
            ? translate(
                  translations,
                  'trav-dash/trav-home',
                  'messages',
                  'th.inbox.conversation.tripSummary.guests.single.adult'
              )
            : translate(
                  translations,
                  'trav-dash/trav-home',
                  'messages',
                  'th.inbox.conversation.tripSummary.guests.multi.adults',
                  adultsCount
              );
    const childrenLabel =
        childrenCount === 1
            ? translate(
                  translations,
                  'trav-dash/trav-home',
                  'messages',
                  'th.inbox.conversation.tripSummary.guests.single.child'
              )
            : translate(
                  translations,
                  'trav-dash/trav-home',
                  'messages',
                  'th.inbox.conversation.tripSummary.guests.multi.children',
                  childrenCount
              );
    const guestLabel = translate(
        translations,
        'ui-development/shared',
        'stabGuestSelector',
        'shared.stabGuestSelector.guests.displayTotal',
        {GUESTS: adultsCount + childrenCount, PETS: 0}
    );

    let isSubmitButtonDisabled = false;
    if (adultsCount === numberOfAdults && childrenCount === numberOfChildren) {
        isSubmitButtonDisabled = true;
    }

    const formPrefix = (
        <MaxGuests
            adultsMax={adultsMax}
            isChildrenAllowed={isChildrenAllowed}
            sleeps={sleeps}
            translations={translations}
        />
    );

    return (
        <GuestPicker
            actionLocation="modify_booking"
            adultsCount={adultsCount}
            adultsLabel={adultsLabel}
            adultsMax={maxAdultsForCounter}
            adultsMin={1}
            applyButtonLabel={translate(
                translations,
                'trav-dash/trav-home',
                'messages',
                'th.button.apply'
            )}
            centered
            childrenCount={childrenCount}
            childrenLabel={childrenLabel}
            childrenMax={maxChildrenForCounter}
            childrenMin={0}
            className="ModalRequest__trip-details--guests"
            continueButtonLabel={translate(
                translations,
                'ui-development/shared',
                'stabGuestSelector',
                'shared.stabGuestSelector.done'
            )}
            disableButton={isSubmitButtonDisabled}
            disableTextInput
            fadeOutDuration={400}
            formPrefixElement={formPrefix}
            formSuffixElement={null}
            guestLabel={guestLabel}
            id={'modify-booking-guest-picker'}
            isOpen={false}
            label={translate(
                translations,
                'ui-development/shared',
                'stabGuestSelector',
                'shared.stabGuestSelector.guests'
            )}
            modalTitle={translate(
                translations,
                'ui-development/shared',
                'stabGuestSelector',
                'shared.stabGuestSelector.guests.selectGuests'
            )}
            mobileBreakpoint={767}
            name={translate(
                translations,
                'ui-development/shared',
                'stabGuestSelector',
                'shared.stabGuestSelector.guests'
            )}
            onChange={onGuestValuesChange}
            onClose={updateGuests}
            onExit={updateGuests}
            petIncluded={false}
            placement={'bottom'}
            positionIconRight={false}
            showClearButton={false}
            showContinue
        />
    );
}

ModifyBookingGuestPicker.propTypes = {
    houseRules: PropTypes.shape({
        children: PropTypes.shape({
            allowed: PropTypes.bool,
        }),
        maxOccupany: PropTypes.shape({
            adults: PropTypes.number,
            children: PropTypes.number,
        }),
    }),
    setUpdatedGuests: PropTypes.func,
    sleeps: PropTypes.number,
    translations: PropTypes.shape({}).isRequired,
};
